import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { signInWithEmailPassword, signInWithSocial, signUpWithEmailPassword } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import logoImg from '../../assets/logo.svg';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { isInRnWebView } from '../../bridge/webviewBridge';

const isIosWebView = () => {
  return isInRnWebView() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#000" d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-2.12 4.45-3.74 4.25z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const KakaoIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="24" fill="#FEE500"/>
    <path fill="#3C1E1E" d="M24 12c-7.73 0-14 4.95-14 11.06 0 3.94 2.62 7.39 6.56 9.34l-1.68 6.14c-.13.47.41.84.82.57l7.34-4.84c.32.02.64.04.96.04 7.73 0 14-4.95 14-11.06S31.73 12 24 12z"/>
  </svg>
);

const AUTO_LOGIN_KEY = 'classcare.autoLogin';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // AuthContext에서 소셜 로그인 처리 완료 시 → SplashPage로 이동 (라우팅 분기)
  useEffect(() => {
    if (currentUser) {
      navigate('/', { replace: true });
    }
  }, [currentUser, navigate]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [autoLogin, setAutoLogin] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(AUTO_LOGIN_KEY));
      return !!saved;
    } catch { return false; }
  });

  // 다른 페이지에서 전달된 토스트 메시지
  useEffect(() => {
    if (location.state?.toast) {
      setToast(location.state.toast);
      // state 정리
      window.history.replaceState({}, '');
      const timer = setTimeout(() => setToast(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    try {
      // 1) Firestore에서 loginId로 사용자 조회
      const q = query(collection(db, 'users'), where('loginId', '==', email.trim()));
      const snap = await getDocs(q);

      if (!snap.empty) {
        // loginId로 찾은 경우 → Firestore 비밀번호 확인
        const userDoc = snap.docs[0];
        const userData = userDoc.data();

        if (userData.password !== password) {
          setError('아이디 또는 비밀번호가 올바르지 않습니다.');
          return;
        }

        // 승인 상태 체크
        if (userData.status && userData.status !== 'approved') {
          showToast('승인 대기중입니다.\n관리자 승인 후 이용 가능합니다.');
          return;
        }

        // Firebase Auth 계정이 연결되어 있으면 그걸로 로그인
        // 없으면 자동 생성 후 Firestore 문서에 authUid 저장
        const fakeEmail = `${email.trim()}@classmanage.local`;

        let authUid;
        try {
          const result = await signInWithEmailPassword({ email: fakeEmail, password, skipEnsure: true });
          authUid = result.uid;
        } catch (authErr) {
          // Firebase Auth 계정이 없으면 생성
          const newUser = await signUpWithEmailPassword({ email: fakeEmail, password });
          authUid = newUser.uid;
        }
        // 매번 authUid 동기화 (DB 초기화 등으로 누락될 수 있음)
        if (authUid && userData.authUid !== authUid) {
          await updateDoc(doc(db, 'users', userDoc.id), { authUid });
        }

        // 자동 로그인 저장
        if (autoLogin) {
          localStorage.setItem(AUTO_LOGIN_KEY, JSON.stringify({ loginId: email.trim(), password }));
        } else {
          localStorage.removeItem(AUTO_LOGIN_KEY);
        }

        // 로그인 성공 → 스플래시로 (가드가 phoneE164/status 분기 처리)
        navigate('/', { replace: true });
      } else {
        // loginId로 못 찾음 → 등록되지 않은 사용자
        showToast('등록되지 않은 아이디입니다.\n회원가입을 진행해주세요.');
      }
    } catch (err) {
      console.error('로그인 실패:', err);
      setError('로그인에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  const handleSocial = async (provider) => {
    setBusy(true);
    setError('');
    try {
      await signInWithSocial({ provider });
      // 소셜 로그인 성공 → 스플래시로 (가드가 분기 처리)
      navigate('/', { replace: true });
    } catch (err) {
      console.error('소셜 로그인 실패:', err);
      setError(err.message || '소셜 로그인에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleSignUp = () => {
    navigate('/phone-verify', { state: { fromSocial: false } });
  };

  return (
    <Container>
      {busy && <FullScreenLoading><Spinner /><LoadingText>로그인 중...</LoadingText></FullScreenLoading>}
      {toast && <Toast>{toast}</Toast>}

      <LogoArea>
        <LogoWrap>
          <LogoImage src={logoImg} alt="클래스케어" />
          <Sparkle>✦</Sparkle>
        </LogoWrap>
        <AppTitle>클래스케어</AppTitle>
        <AppSubtitle>스마트 학원 관리</AppSubtitle>
      </LogoArea>

      <FormArea>
        <form onSubmit={handleLogin}>
          <Label>아이디</Label>
          <Input
            type="text"
            placeholder=""
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Label>비밀번호</Label>
          <Input
            type="password"
            placeholder=""
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <AutoLoginRow>
            <AutoLoginCheckbox
              type="checkbox"
              id="autoLogin"
              checked={autoLogin}
              onChange={(e) => {
                setAutoLogin(e.target.checked);
                if (!e.target.checked) localStorage.removeItem(AUTO_LOGIN_KEY);
              }}
            />
            <AutoLoginLabel htmlFor="autoLogin">자동 로그인</AutoLoginLabel>
          </AutoLoginRow>

          {error && <ErrorText>{error}</ErrorText>}

          <LoginButton type="submit" disabled={busy}>
            {busy ? '로그인 중...' : '로그인'}
          </LoginButton>
        </form>

        <FindLinks>
          <FindLink onClick={() => navigate('/find-id')}>아이디 찾기</FindLink>
          <FindDivider>|</FindDivider>
          <FindLink onClick={() => navigate('/find-password')}>비밀번호 찾기</FindLink>
        </FindLinks>

        <Divider><DividerText>간편 로그인</DividerText></Divider>

        <SocialRow>
          <SocialButton onClick={() => handleSocial('google')} disabled={busy}>
            <GoogleIcon />
          </SocialButton>
          {!isIosWebView() && (
            <SocialButton $kakao onClick={() => handleSocial('kakao')} disabled={busy}>
              <KakaoIcon />
            </SocialButton>
          )}
          {isIosWebView() && (
            <SocialButton $apple onClick={() => handleSocial('apple')} disabled={busy}>
              <AppleIcon />
            </SocialButton>
          )}
        </SocialRow>

        <SignUpArea>
          계정이 없으신가요? <SignUpLink onClick={handleSignUp}>회원가입</SignUpLink>
        </SignUpArea>
      </FormArea>
    </Container>
  );
};

export default LoginPage;

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  background: linear-gradient(180deg, #EEF2FF 0%, #F5F7FA 40%);
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 440px;
  margin: 0 auto;
`;

const FullScreenLoading = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 9999;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 4px solid #E5E7EB;
  border-top-color: #4A7CFE;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoadingText = styled.p`
  font-size: 15px;
  color: #6B7280;
`;

const Toast = styled.div`
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  z-index: 999;
  text-align: center;
  line-height: 1.6;
  white-space: pre-line;
  min-width: 240px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: fadeIn 0.3s;
  @keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(-10px); } }
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LogoWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Sparkle = styled.span`
  position: absolute;
  top: 14px;
  right: 18px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  opacity: 0;
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.8));
  animation: sparkleOnce 2.5s ease-in-out 0.8s forwards;
  @keyframes sparkleOnce {
    0% { opacity: 0; transform: scale(0.3) rotate(-20deg); }
    20% { opacity: 1; transform: scale(1.3) rotate(10deg); }
    50% { opacity: 0.8; transform: scale(1) rotate(5deg); }
    80% { opacity: 1; transform: scale(1.2) rotate(10deg); }
    100% { opacity: 0; transform: scale(0.5) rotate(0deg); }
  }
`;

const LogoImage = styled.img`
  width: 80px;
  height: 80px;
  margin: 0 auto 10px;
  display: block;
  border-radius: 22px;
  box-shadow: 0 8px 32px rgba(74, 124, 254, 0.25);
  animation: glow 3s ease-in-out infinite;
  @keyframes glow {
    0%, 100% { box-shadow: 0 8px 32px rgba(74, 124, 254, 0.25); }
    50% { box-shadow: 0 8px 40px rgba(74, 124, 254, 0.45), 0 0 20px rgba(74, 124, 254, 0.15); }
  }
`;

const AppTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.title};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AppSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`;

const FormArea = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
  margin-top: 12px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  background: white;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.hint};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const AutoLoginRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const AutoLoginCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const AutoLoginLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.accent.red};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 8px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 14px 0 10px;
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const DividerText = styled.span`
  padding: 0 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
  white-space: nowrap;
`;

const SocialRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const SocialButton = styled.button`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid ${({ $kakao, $apple }) => $kakao ? '#FEE500' : $apple ? '#000' : '#ddd'};
  background: ${({ $kakao, $apple }) => $kakao ? '#FEE500' : $apple ? '#000' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
  &:disabled { opacity: 0.5; }
  svg path { fill: ${({ $apple }) => $apple ? '#fff' : undefined}; }
`;

const FindLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`;

const FindLink = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const FindDivider = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const SignUpArea = styled.p`
  text-align: center;
  margin-top: 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SignUpLink = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
`;
